import { useState, useEffect } from "react"

import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  CircularProgress,
  Chip,
  Divider,
} from '@mui/material'

import {
  settingsService,
  type OpenAITestResult,
  type OpenAIBillingResult,
} from "../../api/services/settings"

export default function OpenAIConnectionTest() {
  const [testResult, setTestResult] = useState<OpenAITestResult | null>(null)
  const [billingResult, setBillingResult] = useState<OpenAIBillingResult | null>(null)
  const [isTesting, setIsTesting] = useState(false)
  const [isLoadingBilling, setIsLoadingBilling] = useState(false)

  useEffect(() => {
    const fetchBilling = async () => {
      setIsLoadingBilling(true)
      try {
        const result = await settingsService.getOpenAIBilling()
        setBillingResult(result)
      } catch {
        setBillingResult({ success: false, message: "Failed to fetch billing info" })
      } finally {
        setIsLoadingBilling(false)
      }
    }

    fetchBilling()
  }, [])

  const handleTest = async () => {
    setIsTesting(true)
    setTestResult(null)
    try {
      const result = await settingsService.testOpenAIConnection()
      setTestResult(result)
    } catch {
      setTestResult({ success: false, message: "Request failed" })
    } finally {
      setIsTesting(false)
    }
  }

  return (
    <Card>
      <CardContent sx={{ p: 4 }}>
        <Typography variant="h6" gutterBottom>
          OpenAI Connection
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Test the connection to OpenAI API and view billing information
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Button
            variant="outlined"
            onClick={handleTest}
            disabled={isTesting}
            startIcon={isTesting ? <CircularProgress size={16} /> : null}
          >
            {isTesting ? "Testing..." : "Test Connection"}
          </Button>

          {testResult && (
            <Chip
              label={testResult.success ? "Connected" : "Failed"}
              color={testResult.success ? "success" : "error"}
              size="small"
            />
          )}
        </Box>

        {testResult && (
          <Box
            sx={{
              p: 2,
              borderRadius: 1,
              bgcolor: testResult.success ? 'success.50' : 'error.50',
              border: 1,
              borderColor: testResult.success ? 'success.200' : 'error.200',
              mb: 3,
            }}
          >
            <Typography variant="body2" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
              {testResult.message}
            </Typography>
            {testResult.model && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Model: {testResult.model} | Response time: {testResult.responseTimeMs}ms
              </Typography>
            )}
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" gutterBottom>
          Billing
        </Typography>

        {isLoadingBilling && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={14} />
            <Typography variant="body2" color="text.secondary">Loading billing info...</Typography>
          </Box>
        )}

        {billingResult && !isLoadingBilling && (
          <Box>
            {billingResult.success ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="body2">
                  <strong>Last 30 days:</strong> ${billingResult.totalSpent?.toFixed(2)}
                </Typography>
                {billingResult.dailyCosts && billingResult.dailyCosts.length > 0 && (
                  <Box
                    sx={{
                      mt: 1,
                      maxHeight: 200,
                      overflowY: 'auto',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.5,
                    }}
                  >
                    {billingResult.dailyCosts.map((day) => (
                      <Box key={day.date} sx={{ display: 'flex', justifyContent: 'space-between', maxWidth: 250 }}>
                        <Typography variant="caption" color="text.secondary">
                          {day.date}
                        </Typography>
                        <Typography variant="caption">
                          ${day.amount.toFixed(2)}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {billingResult.message}
              </Typography>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  )
}
